import {
  LanguageModelV2,
  LanguageModelV2CallOptions,
  LanguageModelV2CallWarning,
  LanguageModelV2FinishReason,
  LanguageModelV2StreamPart,
} from '@ai-sdk/provider';
import {
  FetchFunction,
  Resolvable,
  combineHeaders,
  createEventSourceResponseHandler,
  createJsonResponseHandler,
  postJsonToApi,
  resolve,
  injectJsonInstructionIntoMessages,
} from '@ai-sdk/provider-utils';
import { z } from 'zod/v4';
import { convertToCloudCodeMessages } from './convert-to-cloud-code-messages';
import { mapCloudCodeFinishReason } from './map-cloud-code-finish-reason';
import {
  GoogleCloudCodeModelId,
  GoogleCloudCodeSettings,
} from './google-cloud-code-settings';
import { cloudCodeFailedResponseHandler } from './google-cloud-code-error';
import { prepareTools } from './google-cloud-code-prepare-tools';

type GoogleCloudCodeConfig = {
  provider: string;
  baseURL: string;
  headers: Resolvable<Record<string, string | undefined>>;
  getProjectId: () => Promise<string | undefined>;
  fetch?: FetchFunction;
};

export class GoogleCloudCodeLanguageModel implements LanguageModelV2 {
  readonly specificationVersion = 'v2';
  readonly supportedUrls: Record<string, RegExp[]> = { '*/*': [/^https:\/\//] };

  readonly modelId: GoogleCloudCodeModelId;
  readonly settings: GoogleCloudCodeSettings;

  private readonly config: GoogleCloudCodeConfig;

  constructor(
    modelId: GoogleCloudCodeModelId,
    settings: GoogleCloudCodeSettings,
    config: GoogleCloudCodeConfig,
  ) {
    this.modelId = modelId;
    this.settings = settings;
    this.config = config;
  }

  get provider(): string {
    return this.config.provider;
  }

  private async getArgs(options: LanguageModelV2CallOptions) {
    const {
      prompt,
      maxOutputTokens,
      temperature,
      topP,
      topK,
      frequencyPenalty,
      presencePenalty,
      stopSequences,
      responseFormat,
      seed,
      tools,
      toolChoice,
      providerOptions,
    } = options;

    const warnings: LanguageModelV2CallWarning[] = [];

    if (frequencyPenalty != null) {
      warnings.push({ type: 'unsupported-setting', setting: 'frequencyPenalty' });
    }
    if (presencePenalty != null) {
      warnings.push({ type: 'unsupported-setting', setting: 'presencePenalty' });
    }
    if (seed != null) {
      warnings.push({ type: 'unsupported-setting', setting: 'seed' });
    }

    const projectId = await this.config.getProjectId();
    if (!projectId) {
      throw new Error('Project ID is required for Google Cloud Code API');
    }

    const googleProviderOptions = (providerOptions?.['google-cloud-code'] ?? {}) as Record<string, unknown>;
    const generationConfig: Record<string, unknown> = {
      temperature,
      topP,
      topK: topK ?? this.settings.topK,
      maxOutputTokens,
      stopSequences,
      responseMimeType: responseFormat?.type === 'json' ? 'application/json' : undefined,
      // Reduce thought tokens by default to avoid hitting MAX_TOKENS before tool calls/completions
      thinkingConfig: (googleProviderOptions as any).thinkingConfig ?? { includeThoughts: false },
    };

    const { tools: ccTools, toolConfig, toolWarnings } = prepareTools({ tools, toolChoice });
    warnings.push(...toolWarnings);

    // If JSON schema is provided, inject it into the prompt for models that don't support native schemas
    const promptWithSchema = responseFormat?.type === 'json'
      ? injectJsonInstructionIntoMessages({
          messages: prompt,
          schema: responseFormat.schema,
        })
      : prompt;

    const converted = convertToCloudCodeMessages(promptWithSchema);
    const baseRequest = {
      contents: converted.contents,
      systemInstruction: converted.systemInstruction,
      generationConfig,
      safetySettings: this.settings.safetySettings,
      tools: ccTools,
      toolConfig,
    };

    const args = {
      model: this.modelId,
      project: projectId,
      request: baseRequest,
    } as const;

    return { args, warnings };
  }

  async doGenerate(
    options: LanguageModelV2CallOptions,
  ): Promise<Awaited<ReturnType<LanguageModelV2['doGenerate']>>> {
    const { args, warnings } = await this.getArgs(options);

    let headers;
    try {
      headers = await resolve(this.config.headers);
    } catch (error) {
      throw new Error(
        `Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }

    const {
      responseHeaders,
      value: response,
      rawValue: rawResponse,
    } = await postJsonToApi({
      url: `${this.config.baseURL}/v1internal:generateContent`,
      headers: combineHeaders(headers, options.headers),
      body: args,
      failedResponseHandler: cloudCodeFailedResponseHandler,
      successfulResponseHandler: createJsonResponseHandler(
        cloudCodeGenerateContentResponseSchema,
      ),
      abortSignal: options.abortSignal,
      fetch: this.config.fetch,
    });

    // no-op

    // Extract the actual response from the Cloud Code wrapper
    const actualResponse = response.response;
    const candidate = actualResponse.candidates?.[0];

    if (!candidate) {
      throw new Error('No candidates in response');
    }

    const content = candidate.content;
    if (!content || !Array.isArray((content as any).parts)) {
      return {
        content: [],
        finishReason: mapCloudCodeFinishReason(candidate.finishReason),
        usage: {
          inputTokens: actualResponse.usageMetadata?.promptTokenCount ?? undefined,
          outputTokens: actualResponse.usageMetadata?.candidatesTokenCount ?? undefined,
          totalTokens: actualResponse.usageMetadata?.totalTokenCount ?? undefined,
        },
        request: { body: args },
        response: {
          headers: responseHeaders,
          body: rawResponse,
        },
        warnings,
      };
    }

    const generatedContent: Array<
      { type: 'text'; text: string } |
      { type: 'tool-call'; toolCallId: string; toolName: string; input: string }
    > = [];

    for (const part of (content as any).parts as Array<any>) {
      if (part.text != null) {
        generatedContent.push({ type: 'text', text: part.text });
      }
      if (part.functionCall != null) {
        generatedContent.push({
          type: 'tool-call',
          toolCallId: crypto.randomUUID(),
          toolName: part.functionCall.name,
          input: JSON.stringify(part.functionCall.args ?? {}),
        });
      }
    }

    return {
      content: generatedContent,
      finishReason: mapCloudCodeFinishReason(candidate.finishReason),
      usage: {
        inputTokens: actualResponse.usageMetadata?.promptTokenCount ?? undefined,
        outputTokens: actualResponse.usageMetadata?.candidatesTokenCount ?? undefined,
        totalTokens: actualResponse.usageMetadata?.totalTokenCount ?? undefined,
      },
      request: { body: args },
      response: {
        headers: responseHeaders,
        body: rawResponse,
      },
      warnings,
    };
  }

  async doStream(
    options: LanguageModelV2CallOptions,
  ): Promise<Awaited<ReturnType<LanguageModelV2['doStream']>>> {
    const { args, warnings } = await this.getArgs(options);

    let headers;
    try {
      headers = await resolve(this.config.headers);
    } catch (error) {
      throw new Error(
        `Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }

    const { responseHeaders, value: response } = await postJsonToApi({
      url: `${this.config.baseURL}/v1internal:streamGenerateContent?alt=sse`,
      headers: combineHeaders(headers, options.headers),
      body: args,
      failedResponseHandler: cloudCodeFailedResponseHandler,
      successfulResponseHandler: createEventSourceResponseHandler(
        cloudCodeStreamContentChunkSchema,
      ),
      abortSignal: options.abortSignal,
      fetch: this.config.fetch,
    });

    // no-op

    let finishReason: LanguageModelV2FinishReason = 'unknown';
    let usage: { inputTokens: number | undefined; outputTokens: number | undefined; totalTokens: number | undefined } = {
      inputTokens: undefined,
      outputTokens: undefined,
      totalTokens: undefined,
    };

    const stream = new ReadableStream<LanguageModelV2StreamPart>({
      async start(controller) {
        controller.enqueue({ type: 'stream-start', warnings });
        const reader = response.getReader();
        try {
          while (true) {
            const { value: chunk, done } = await reader.read();
            if (done) break;
            if (!chunk.success) {
              controller.enqueue({ type: 'error', error: chunk.error });
              continue;
            }

            const value = chunk.value;
            const actualResponse = value.response;
            if (!actualResponse) continue;

            if (actualResponse.usageMetadata) {
              usage = {
                inputTokens: actualResponse.usageMetadata.promptTokenCount ?? undefined,
                outputTokens: actualResponse.usageMetadata.candidatesTokenCount ?? undefined,
                totalTokens: actualResponse.usageMetadata.totalTokenCount ?? undefined,
              };
            }

            const candidate = actualResponse.candidates?.[0];
            if (!candidate) continue;

            if (candidate.finishReason) {
              finishReason = mapCloudCodeFinishReason(candidate.finishReason);
            }

            const content = candidate.content;
            if (!content || !content.parts) continue;

            for (const part of content.parts as Array<any>) {
              if (part.text != null) {
                const id = crypto.randomUUID();
                controller.enqueue({ type: 'text-start', id });
                controller.enqueue({ type: 'text-delta', id, delta: part.text });
                controller.enqueue({ type: 'text-end', id });
              }

              if (part.functionCall != null) {
                const toolCallId = crypto.randomUUID();
                controller.enqueue({
                  type: 'tool-call',
                  toolCallId,
                  toolName: part.functionCall.name,
                  input: JSON.stringify(part.functionCall.args ?? {}),
                });
              }
            }
          }
        } finally {
          controller.enqueue({ type: 'finish', finishReason, usage });
          controller.close();
        }
      },
    });

    return {
      stream,
      request: { body: args },
      response: { headers: responseHeaders },
    };
  }
}

// Response schemas for Cloud Code API
const cloudCodeContentSchema = z.object({
  role: z.string(),
  parts: z
    .array(
      z.object({
        text: z.string().optional(),
        functionCall: z
          .object({
            name: z.string(),
            args: z.record(z.string(), z.any()).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

const cloudCodeCandidateSchema = z.object({
  content: cloudCodeContentSchema,
  finishReason: z.string().optional(),
});

const cloudCodeUsageMetadataSchema = z.object({
  promptTokenCount: z.number().optional(),
  candidatesTokenCount: z.number().optional(),
  totalTokenCount: z.number().optional(),
});

const cloudCodeGenerateContentResponseSchema = z.object({
  response: z.object({
    candidates: z.array(cloudCodeCandidateSchema).optional(),
    promptFeedback: z.any().optional(),
    usageMetadata: cloudCodeUsageMetadataSchema.optional(),
  }),
});

const cloudCodeStreamContentChunkSchema = z.object({
  response: z.object({
    candidates: z.array(cloudCodeCandidateSchema).optional(),
    usageMetadata: cloudCodeUsageMetadataSchema.optional(),
  }).optional(),
});